import { cronToHuman, getCronPresets, validateCronExpression } from "@/common/helpers/cron-to-human";
import { cn } from "@/common/helpers/utils";
import { ButtonColorVariant, ButtonVariant, buttonVariants } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input, inputVariants } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertCircleIcon, CalendarClockIcon, CheckCircleIcon } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

interface CronJobPickerProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

export function CronJobPicker({ value, onChange, disabled, className }: CronJobPickerProps) {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const [selectedPreset, setSelectedPreset] = useState<string>("custom");
  const inputId = useId();
  const presetSelectId = useId();

  const presets = getCronPresets();
  const isValid = validateCronExpression(tempValue);
  const humanReadable = isValid ? cronToHuman(tempValue) : t("InvalidCronExpression");

  const handlePresetChange = (presetValue: string): void => {
    if (presetValue === "custom") {
      setSelectedPreset("custom");
      return;
    }
    setSelectedPreset(presetValue);
    setTempValue(presetValue);
  };

  const handleCustomInput = (newValue: string): void => {
    setTempValue(newValue);
    setSelectedPreset("custom");
  };

  const handleApply = (): void => {
    if (isValid) {
      onChange(tempValue);
      setIsOpen(false);
    }
  };

  const handleCancel = (): void => {
    setTempValue(value);
    setSelectedPreset("custom");
    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean): void => {
    if (open) {
      setTempValue(value);
      const matchingPreset = presets.find((p) => p.value === value);
      setSelectedPreset(matchingPreset ? matchingPreset.value : "custom");
    }
    setIsOpen(open);
  };

  return (
    <div className={className}>
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <DialogTrigger asChild>
                <button
                  className={cn(
                    inputVariants(),
                    "hover:bg-accent flex h-9 cursor-pointer items-center px-3 py-1 text-sm",
                  )}
                  disabled={disabled}
                  type="button"
                >
                  <CalendarClockIcon className="mr-2 h-4 w-4" />
                  <span className="truncate">{value || t("SelectCronSchedule")}</span>
                </button>
              </DialogTrigger>
            </TooltipTrigger>
            <TooltipContent>
              <p className="font-semibold">{cronToHuman(value)}</p>
              <p className="text-muted-foreground mt-1 text-xs">{value}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <DialogContent className="sm:max-w-150">
          <DialogHeader>
            <DialogTitle>{t("ConfigureCronSchedule")}</DialogTitle>
            <DialogDescription>{t("ConfigureCronScheduleDescription")}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Preset Selection */}
            <div className="grid gap-2">
              <Label htmlFor={presetSelectId}>{t("PredefinedSchedules")}</Label>
              <Select value={selectedPreset} onValueChange={handlePresetChange}>
                <SelectTrigger id={presetSelectId}>
                  <SelectValue placeholder={t("Custom")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">{t("Custom")}</SelectItem>
                  {presets.map((preset) => (
                    <SelectItem key={preset.value} value={preset.value}>
                      {preset.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Custom Input */}
            <div className="grid gap-2">
              <Label htmlFor={inputId}>{t("CronExpression6Fields")}</Label>
              <Input
                id={inputId}
                value={tempValue}
                onChange={(e) => handleCustomInput(e.target.value)}
                placeholder="0 0 2 * * *"
                className="font-mono"
                error={!isValid}
              />
              <p className="text-muted-foreground text-xs">{t("CronFormat")}</p>
            </div>

            {/* Validation and Preview */}
            <div className="border-border bg-muted/30 rounded-lg border p-4">
              <div className="flex items-start gap-2">
                {isValid ? (
                  <CheckCircleIcon className="text-success dark:text-success mt-0.5 h-5 w-5 shrink-0" />
                ) : (
                  <AlertCircleIcon className="text-error mt-0.5 h-5 w-5 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="mb-1 text-sm font-medium">{isValid ? t("ValidExpression") : t("InvalidExpression")}</p>
                  <p className="text-muted-foreground text-sm wrap-break-word">{humanReadable}</p>
                  {isValid && <p className="text-muted-foreground mt-2 font-mono text-xs break-all">{tempValue}</p>}
                </div>
              </div>
            </div>

            {/* Examples */}
            <div className="text-muted-foreground space-y-1">
              <p className="mb-2 text-base font-medium">{t("Examples")}</p>
              <div className="flex flex-row items-start gap-4 text-sm sm:gap-8">
                <ul className="flex flex-col items-start justify-center gap-2">
                  <li>{t("DailyAt2AM")}</li>
                  <li>{t("Every5Minutes")}</li>
                  <li>{t("WeekdaysAt8AM")}</li>
                  <li>{t("MonthlyFirstDay2AM")}</li>
                </ul>
                <div className="font-mono">
                  <div className="grid grid-cols-6 gap-x-0 gap-y-2 sm:gap-x-3">
                    <span className="text-center">0</span>
                    <span className="text-center">0</span>
                    <span className="text-center">2</span>
                    <span className="text-center">*</span>
                    <span className="text-center">*</span>
                    <span className="text-center">*</span>

                    <span className="text-center">0</span>
                    <span className="text-center">*/5</span>
                    <span className="text-center">*</span>
                    <span className="text-center">*</span>
                    <span className="text-center">*</span>
                    <span className="text-center">*</span>

                    <span className="text-center">0</span>
                    <span className="text-center">0</span>
                    <span className="text-center">8</span>
                    <span className="text-center">*</span>
                    <span className="text-center">*</span>
                    <span className="text-center">1-5</span>

                    <span className="text-center">0</span>
                    <span className="text-center">0</span>
                    <span className="text-center">2</span>
                    <span className="text-center">1</span>
                    <span className="text-center">*</span>
                    <span className="text-center">*</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <button
              className={buttonVariants({
                variant: ButtonVariant.Outlined,
                colorVariant: ButtonColorVariant.Secondary,
              })}
              onClick={handleCancel}
              type="button"
            >
              {t("Cancel")}
            </button>
            <button className={buttonVariants()} onClick={handleApply} disabled={!isValid} type="button">
              {t("Apply")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
